/**
 * Available dragger/tool types.
 */
export enum DraggerType {
  None = "",
  Pan = "pan",
  Freehand = "freehand",
  Line = "line",
  Polyline = "polyline",
  Rectangle = "rectangle",
  Circle = "circle",
  Ellipse = "ellipse",
  Text = "text",
  Handwriting = "handwriting",
  StylusKeyboard = "stylusKeyboard",
  Block = "block",
  PhotoMarker = "photoMarker",
  Leader = "leader",
  Select = "select",
  // Base-point edit tools (BasePointDragger): act on the current selection
  // rather than creating anything, and are launched from SelectionMenu rather
  // than the tool toolbar.
  Copy = "copy",
  Move = "move",
  Rotate = "rotate",
  Scale = "scale",
  Measure = "measure",
  Origin = "origin",
  ZoomWindow = "zoomWindow",
  Eraser = "eraser",
}

export function findDraggerType(str: string): DraggerType {
  const match = Object.entries(DraggerType).find(([, value]) => value === str);
  if (match) {
    return match[1];
  } else {
    return DraggerType.None;
  }
}

export type { Point2D, Point3D } from "../types/types";
import type { Point3D } from "../types/types";
import type { SnapOptions } from "../snap/types";
export type { SnapOptions };

/**
 * Live state of the segment currently being placed by a sequential dragger
 * (last fixed vertex + delta to the current drag/typed position).
 */
export interface DraftSegmentInfo {
  /** Last fixed vertex (segment start), in world coordinates. */
  origin: Point3D;
  /** World-space delta X from origin. */
  dx: number;
  /** World-space delta Y from origin. */
  dy: number;
  /** Segment length (world units). */
  length: number;
  /** Segment angle in degrees, 0=East, atan2 convention. */
  angleDeg: number;
}

/**
 * One numeric field a dragger accepts as typed input at its current step.
 * Structurally compatible with `KeypadField` (NumericKeypadModal.vue) so a spec
 * can be handed straight to the pad; declared here rather than imported so the
 * drawing layer never depends on a component.
 */
export interface TypedInputField {
  key: string;
  /** Short text shown on the pad's tab. Ignored when `icon` is set. */
  label?: string;
  /** Phosphor icon name shown on the tab instead of `label`. */
  icon?: string;
  /** Whether the pad's sign button applies. Defaults to true. */
  allowNegative?: boolean;
}

/**
 * A read-only number worth keeping in sight while typing — SCALE uses it to
 * show the reference length it measured, which is what the new length will be
 * divided by. Purely informational: never edited, never committed.
 */
export interface TypedInputHint {
  /** i18n key for the label. */
  labelKey: string;
  value: number;
}

/**
 * One selectable set of fields in the keypad — the "mode" the user is typing in.
 * Placing a point relative to an origin always offers exactly two: XY_MODE and
 * LENGTH_ANGLE_MODE (see relativePointInput.ts). Steps that are neither
 * (ROTATE's angle, SCALE's factor or new length) offer a single mode, and the
 * pad then hides its mode selector entirely.
 */
export interface TypedInputMode {
  /** Stable id; also the i18n suffix for the selector's label. */
  id: string;
  fields: TypedInputField[];
}

/**
 * What a dragger accepts as typed numeric input right now. Step-dependent: the
 * same tool can offer different modes (or none) as it advances — SCALE offers
 * a factor before its reference point is picked and a length after.
 */
export interface TypedInputSpec {
  /** i18n key for the pad's header. */
  titleKey: string;
  /**
   * Identity of the step being typed, when the mode list alone can't express
   * it. The draw tools set it from the origin they measure against, so fixing
   * or undoing a vertex reads as a new step and drops half-typed values that
   * described the previous segment — the mode list is identical throughout, so
   * without this the pad could not tell the two apart.
   */
  stepId?: string;
  /** Selectable field sets. A single entry means "no mode selector". */
  modes: TypedInputMode[];
  /**
   * Live values mirrored from the drag in progress, keyed by field key. A
   * superset covering every mode's fields, so switching mode needs no round
   * trip to the dragger.
   */
  values: Record<string, number>;
  /** Optional read-only context for this step. */
  hint?: TypedInputHint;
}

/**
 * World-space delta for a typed segment entry. Internal to the draw draggers
 * (SequentialDragger / TwoPointDragger) since the keypad now speaks
 * TypedInputSpec; kept because their preview pipeline threads it through as
 * `exactDelta` to avoid a lossy screenToWorld round trip.
 */
export interface SegmentDelta {
  dx: number;
  dy: number;
}

/**
 * Pointer event types that draggers handle.
 */
export type PointerEventType = "pen" | "touch" | "mouse";

/**
 * Dragger configuration options.
 */
export interface DraggerConfig {
  /** Minimum distance in pixels between collected points (for freehand) */
  minPointDistance?: number;
  /** Whether this dragger should only respond to pen input */
  penOnly?: boolean;
  /** Whether to automatically select the entity after creation (default: true) */
  autoSelect?: boolean;
  /** Use SnapOptions to specify how dragger reacts to snap */
  snapOptions?: SnapOptions;
}
